import { STANDARDS_MAP } from "../constants/standardsMap";
import { gradeLabel, hasDirectMastery, masteryLevel } from "../service/mastery";
import { MasteryLevel } from "../types/enums";
import { GRADE_ORDER } from "../types/standards";
import type {
  DomainNode,
  Grade,
  StandardNode,
  StudentMastery,
} from "../types/standards";
import type {
  SummaryContext,
  SummaryDomain,
  SummaryDomainGrade,
  SummaryModel,
  SummaryRow,
  SummarySection,
  SummaryStandard,
  SummaryStats,
  SummaryTarget,
} from "./types";

type CatalogDomain = {
  id: string;
  code: string;
  name: string;
  category?: string;
  grades: Map<Grade, StandardNode[]>;
};

type PreparedStudent = {
  user_id: number;
  name: string;
  grade: Grade | null;
  statuses: Map<string, MasteryLevel>;
  directEvidence: Set<string>;
  directDomains: Set<string>;
  overall: MasteryLevel;
  domains: Map<string, MasteryLevel>;
};

export function summaryStatus(points: number | null): MasteryLevel {
  if (points === null) return MasteryLevel.NOT_STARTED;
  if (points >= 2.5) return MasteryLevel.MASTERED;
  if (points >= 1.5) return MasteryLevel.PROFICIENT;
  return MasteryLevel.APPROACHING;
}

function averageStatus(statuses: Iterable<MasteryLevel>): MasteryLevel {
  return summaryStats([...statuses], "standards").status;
}

export function summaryStats(
  statuses: MasteryLevel[],
  unit: SummaryStats["unit"],
  names?: string[],
  hasDirectEvidence?: boolean,
): SummaryStats {
  const counts: SummaryStats["counts"] = {
    [MasteryLevel.MASTERED]: 0,
    [MasteryLevel.PROFICIENT]: 0,
    [MasteryLevel.APPROACHING]: 0,
    [MasteryLevel.NOT_STARTED]: 0,
  };
  const members: NonNullable<SummaryStats["members"]> = {};
  statuses.forEach((status, index) => {
    counts[status]++;
    if (names) (members[status] ??= []).push(names[index]);
  });
  const total = statuses.length;
  const started = total - counts[MasteryLevel.NOT_STARTED];
  const atLeast =
    counts[MasteryLevel.MASTERED] + counts[MasteryLevel.PROFICIENT];
  const points = started
    ? (3 * counts[MasteryLevel.MASTERED] +
        2 * counts[MasteryLevel.PROFICIENT] +
        counts[MasteryLevel.APPROACHING]) /
      started
    : null;
  return {
    counts,
    total,
    started,
    atLeast,
    percent: started ? Math.round((100 * atLeast) / started) : null,
    points,
    status: summaryStatus(points),
    unit,
    ...(hasDirectEvidence === undefined ? {} : { hasDirectEvidence }),
    ...(names ? { members } : {}),
  };
}

export function summaryRootTarget(context: SummaryContext): SummaryTarget {
  return context.viewer.role === "ORG_ADMIN"
    ? { level: "school" }
    : {
        level: "teacher",
        teacherUserId: context.viewer.teacher_user_id ?? context.viewer.user_id,
      };
}

function indexCatalog(catalog: DomainNode[]) {
  const domains = new Map<string, CatalogDomain>();
  const byGrade = new Map<Grade, StandardNode[]>();
  for (const domain of catalog) {
    if (!domain.grade) continue;
    const key = domain.category ? domain.id : domain.code;
    let merged = domains.get(key);
    if (!merged) {
      merged = {
        id: key,
        code: domain.code,
        name: domain.name,
        category: domain.category,
        grades: new Map(),
      };
      domains.set(key, merged);
    }
    const standards = domain.clusters.flatMap((cluster) => cluster.standards);
    merged.grades.set(domain.grade, [
      ...(merged.grades.get(domain.grade) ?? []),
      ...standards,
    ]);
    byGrade.set(domain.grade, [
      ...(byGrade.get(domain.grade) ?? []),
      ...standards,
    ]);
  }
  return { domains: [...domains.values()], byGrade };
}

function gradeOrder(a: Grade, b: Grade) {
  return GRADE_ORDER.indexOf(a) - GRADE_ORDER.indexOf(b);
}

function sortedRows(rows: SummaryRow[]): SummaryRow[] {
  return rows.sort((a, b) => {
    const aStarted = a.stats.hasDirectEvidence !== false && a.stats.started > 0;
    const bStarted = b.stats.hasDirectEvidence !== false && b.stats.started > 0;
    if (!aStarted && bStarted) return 1;
    if (aStarted && !bStarted) return -1;
    const byPercent =
      aStarted && bStarted
        ? b.stats.atLeast * a.stats.started - a.stats.atLeast * b.stats.started
        : 0;
    return byPercent || a.name.localeCompare(b.name);
  });
}

function studentDetail(students: PreparedStudent[]): string {
  const grades = [
    ...new Set(
      students.flatMap((student) => (student.grade ? [student.grade] : [])),
    ),
  ].sort(gradeOrder);
  const count = `${students.length} ${students.length === 1 ? "student" : "students"}`;
  if (!grades.length) return count;
  if (grades.length === 1) return `${count}, ${gradeLabel(grades[0])}`;
  return `${count}, grades ${grades[0]} to ${grades[grades.length - 1]}`;
}

export function buildSummaryModel(
  context: SummaryContext,
  masteryStudents: StudentMastery[],
  target: SummaryTarget,
  catalog: DomainNode[] = STANDARDS_MAP,
): SummaryModel {
  if (
    !context.available ||
    (context.viewer.role === "ORG_ADMIN" && !context.organization)
  )
    return { denied: true };
  if (target.level === "school" && context.viewer.role !== "ORG_ADMIN")
    return { denied: true };

  const { domains, byGrade } = indexCatalog(catalog);
  const masteryById = new Map(
    masteryStudents.flatMap((student) =>
      student.user_id === undefined
        ? []
        : [[student.user_id, student] as const],
    ),
  );
  const studentsById = new Map<number, PreparedStudent>();
  for (const student of context.students) {
    if (studentsById.has(student.user_id)) continue;
    const grade =
      student.grade && GRADE_ORDER.includes(student.grade)
        ? student.grade
        : null;
    const cells = masteryById.get(student.user_id)?.cells ?? {};
    const statuses = new Map<string, MasteryLevel>();
    const directEvidence = new Set<string>();
    for (const standard of grade ? (byGrade.get(grade) ?? []) : []) {
      const cell = cells[standard.id];
      if (cell?.evidence > 0) directEvidence.add(standard.id);
      statuses.set(
        standard.id,
        cell && hasDirectMastery(cell)
          ? masteryLevel(cell.mastery, cell.started)
          : MasteryLevel.NOT_STARTED,
      );
    }
    const domainStatuses = new Map<string, MasteryLevel>();
    const directDomains = new Set<string>();
    for (const domain of domains) {
      const standards = grade ? domain.grades.get(grade) : undefined;
      if (standards?.length) {
        if (standards.some((standard) => directEvidence.has(standard.id)))
          directDomains.add(domain.id);
        domainStatuses.set(
          domain.id,
          averageStatus(
            standards.map((standard) => statuses.get(standard.id)!),
          ),
        );
      }
    }
    studentsById.set(student.user_id, {
      ...student,
      grade,
      statuses,
      directEvidence,
      directDomains,
      overall: averageStatus(statuses.values()),
      domains: domainStatuses,
    });
  }

  const classroomStudents = (classrooms: SummaryContext["classrooms"]) => {
    const ids = new Set(
      classrooms.flatMap((classroom) => classroom.student_user_ids),
    );
    return [...ids].flatMap((id) => {
      const student = studentsById.get(id);
      return student ? [student] : [];
    });
  };
  const eligible = (students: PreparedStudent[]) =>
    students.filter((student) => student.grade !== null);
  const ownTeacher =
    context.viewer.role === "TEACHER" ? context.viewer.teacher_user_id : null;
  const teacherClassrooms = (teacherUserId: number) =>
    teacherUserId === ownTeacher
      ? context.classrooms
      : context.classrooms.filter(
          (classroom) => classroom.teacher_user_id === teacherUserId,
        );
  const teacherStudents = (teacherUserId: number) =>
    classroomStudents(teacherClassrooms(teacherUserId));
  const teacherName = (teacherUserId: number) =>
    context.teachers.find((teacher) => teacher.user_id === teacherUserId)
      ?.name ?? "Teacher";
  const isTeacherAccessible = (teacherUserId: number) =>
    context.teachers.some((teacher) => teacher.user_id === teacherUserId) ||
    teacherUserId === ownTeacher;
  if (target.level !== "school" && !isTeacherAccessible(target.teacherUserId))
    return { denied: true };

  let scoped =
    target.level === "school"
      ? classroomStudents(context.classrooms)
      : teacherStudents(target.teacherUserId);
  let group: SummaryContext["classrooms"][number] | undefined;
  if (
    target.level === "group" ||
    (target.level === "student" && target.groupId !== undefined)
  ) {
    group = teacherClassrooms(target.teacherUserId).find(
      (classroom) => classroom.id === target.groupId,
    );
    if (!group) return { denied: true };
    scoped = classroomStudents([group]);
  }
  if (
    target.level === "grade" ||
    (target.level === "student" && target.grade !== undefined)
  ) {
    if (target.level === "student" && target.groupId !== undefined)
      return { denied: true };
    if (!GRADE_ORDER.includes(target.grade!)) return { denied: true };
    scoped = scoped.filter((student) => student.grade === target.grade);
    if (!scoped.length) return { denied: true };
  }
  if (target.level === "student") {
    scoped = scoped.filter(
      (student) => student.user_id === target.studentUserId,
    );
    if (!scoped.length) return { denied: true };
  }

  const students = eligible(scoped);
  const includeNames = target.level === "grade" || target.level === "group";
  const overallStats = (items: PreparedStudent[], names = includeNames) =>
    summaryStats(
      items.map((student) => student.overall),
      "students",
      names ? items.map((student) => student.name) : undefined,
      items.some((student) => student.directEvidence.size > 0),
    );
  const studentStats = (
    student: PreparedStudent,
    codes = [...student.statuses.keys()],
  ) =>
    summaryStats(
      codes.map((code) => student.statuses.get(code)!),
      "standards",
      codes,
      codes.some((code) => student.directEvidence.has(code)),
    );
  const domainStats = (items: PreparedStudent[], domainId: string) =>
    summaryStats(
      items.map(
        (student) => student.domains.get(domainId) ?? MasteryLevel.NOT_STARTED,
      ),
      "students",
      includeNames ? items.map((student) => student.name) : undefined,
      items.some((student) => student.directDomains.has(domainId)),
    );
  const standardsFor = (
    standards: StandardNode[],
    items: PreparedStudent[],
    studentPage: boolean,
  ): SummaryStandard[] =>
    standards.flatMap((standard) => {
      const statuses = items.map(
        (student) =>
          student.statuses.get(standard.id) ?? MasteryLevel.NOT_STARTED,
      );
      const hasDirectEvidence = items.some((student) =>
        student.directEvidence.has(standard.id),
      );
      if (!studentPage && !hasDirectEvidence) return [];
      return [
        {
          id: standard.id,
          description: standard.desc,
          ...(studentPage
            ? {
                status: statuses[0] ?? MasteryLevel.NOT_STARTED,
              }
            : {}),
          stats: summaryStats(
            statuses,
            studentPage ? "standards" : "students",
            includeNames ? items.map((student) => student.name) : undefined,
            hasDirectEvidence,
          ),
        },
      ];
    });

  const scopeGrades = [
    ...new Set(students.map((student) => student.grade!)),
  ].sort(gradeOrder);
  const studentPage = target.level === "student";
  const domainRows: SummaryDomain[] = domains
    .flatMap((domain) => {
      const items = students.filter((student) =>
        domain.grades.has(student.grade!),
      );
      if (!items.length) return [];
      const stats = studentPage
        ? studentStats(
            items[0],
            (domain.grades.get(items[0].grade!) ?? []).map(
              (standard) => standard.id,
            ),
          )
        : domainStats(items, domain.id);
      if (!studentPage && !stats.hasDirectEvidence) return [];
      const grades: SummaryDomainGrade[] = scopeGrades.flatMap((grade) => {
        const standards = domain.grades.get(grade);
        if (!standards?.length) return [];
        const gradeStudents = items.filter(
          (student) => student.grade === grade,
        );
        const gradeStats = studentPage
          ? stats
          : domainStats(gradeStudents, domain.id);
        if (!studentPage && !gradeStats.hasDirectEvidence) return [];
        return [
          {
            grade,
            name: gradeLabel(grade),
            stats: gradeStats,
            standards: standardsFor(standards, gradeStudents, studentPage),
          },
        ];
      });
      return [
        {
          id: domain.id,
          code: domain.code,
          name: domain.name,
          category: domain.category,
          stats,
          grades: scopeGrades.length > 1 ? grades : [],
          standards:
            scopeGrades.length === 1 ? (grades[0]?.standards ?? []) : [],
        },
      ];
    })
    .sort(
      (a, b) =>
        (a.category ? 1 : 0) - (b.category ? 1 : 0) ||
        a.name.localeCompare(b.name),
    );

  const gradeRows = (
    teacherUserId: number,
    items: PreparedStudent[],
  ): SummaryRow[] => {
    const grades = [...new Set(items.map((student) => student.grade!))].sort(
      gradeOrder,
    );
    return grades.map((grade) => {
      const gradeStudents = items.filter((student) => student.grade === grade);
      const stats = overallStats(gradeStudents, false);
      return {
        id: `grade:${teacherUserId}:${grade}`,
        kind: "grade" as const,
        name: gradeLabel(grade),
        detail: `${gradeStudents.length} ${gradeStudents.length === 1 ? "student" : "students"}`,
        stats,
        target: { level: "grade" as const, teacherUserId, grade },
        children: [],
      };
    });
  };

  const sections: SummarySection[] = [];
  if (target.level === "school") {
    const rows: SummaryRow[] = context.teachers.map((teacher) => {
      const items = eligible(teacherStudents(teacher.user_id));
      return {
        id: `teacher:${teacher.user_id}`,
        kind: "teacher",
        name: teacher.name,
        detail: studentDetail(items),
        stats: overallStats(items, false),
        target: { level: "teacher", teacherUserId: teacher.user_id },
        children: gradeRows(teacher.user_id, items),
      };
    });
    sections.push({
      id: "teachers",
      title: "Teachers",
      subtitle:
        "Each teacher's grades are nested below them. Click a teacher for their full view, or a grade for just that grade.",
      rows: sortedRows(rows),
    });
  } else if (target.level === "teacher") {
    sections.push({
      id: "grades",
      title: "Grades",
      rows: gradeRows(target.teacherUserId, students),
    });
    sections.push({
      id: "groups",
      title: "Groups",
      rows: sortedRows(
        teacherClassrooms(target.teacherUserId).map((classroom) => {
          const items = eligible(classroomStudents([classroom]));
          return {
            id: `group:${classroom.id}`,
            kind: "group",
            name: classroom.name,
            detail: studentDetail(items),
            stats: overallStats(items, false),
            target: {
              level: "group",
              teacherUserId: target.teacherUserId,
              groupId: classroom.id,
            },
            children: [],
          };
        }),
      ),
    });
  } else if (target.level === "grade" || target.level === "group") {
    sections.push({
      id: "students",
      title: "Students",
      rows: sortedRows(
        students.map((student) => ({
          id: `student:${student.user_id}`,
          kind: "student",
          name: student.name,
          detail: gradeLabel(student.grade!),
          stats: studentStats(student),
          target: {
            level: "student",
            teacherUserId: target.teacherUserId,
            studentUserId: student.user_id,
            ...(target.level === "grade"
              ? { grade: target.grade }
              : { groupId: target.groupId }),
          },
          children: [],
        })),
      ),
    });
  }

  const breadcrumbs: Array<{ label: string; target: SummaryTarget }> = [];
  if (context.viewer.role === "ORG_ADMIN")
    breadcrumbs.push({
      label: context.organization!.name,
      target: { level: "school" },
    });
  else
    breadcrumbs.push({
      label: teacherName(ownTeacher!),
      target: summaryRootTarget(context),
    });
  if (
    target.level !== "school" &&
    (context.viewer.role === "ORG_ADMIN" || target.teacherUserId !== ownTeacher)
  ) {
    breadcrumbs.push({
      label: teacherName(target.teacherUserId),
      target: { level: "teacher", teacherUserId: target.teacherUserId },
    });
  }
  if (
    target.level === "grade" ||
    (target.level === "student" && target.grade !== undefined)
  ) {
    breadcrumbs.push({
      label: gradeLabel(target.grade!),
      target: {
        level: "grade",
        teacherUserId: target.teacherUserId,
        grade: target.grade!,
      },
    });
  } else if (
    target.level === "group" ||
    (target.level === "student" && target.groupId !== undefined)
  ) {
    breadcrumbs.push({
      label: group!.name,
      target: {
        level: "group",
        teacherUserId: target.teacherUserId,
        groupId: group!.id,
      },
    });
  }
  if (target.level === "student")
    breadcrumbs.push({ label: scoped[0].name, target });

  let title = context.organization?.name ?? "School";
  let detail = studentDetail(students);
  const levelLabel = {
    school: "School",
    teacher: "Teacher",
    grade: "Grade",
    group: "Group",
    student: "Student",
  }[target.level];
  if (target.level === "teacher") title = teacherName(target.teacherUserId);
  if (target.level === "grade") {
    title = gradeLabel(target.grade);
    detail = `${teacherName(target.teacherUserId)} · ${studentDetail(students)}`;
  }
  if (target.level === "group") {
    title = group!.name;
    detail = `${teacherName(group!.teacher_user_id)} · ${studentDetail(students)}`;
  }
  if (target.level === "student") {
    const student = scoped[0];
    title = student.name;
    const classroom =
      group ??
      teacherClassrooms(target.teacherUserId).find((item) =>
        item.student_user_ids.includes(student.user_id),
      );
    detail = `${student.grade ? gradeLabel(student.grade) : "Enrolled grade missing"}. ${classroom ? `${classroom.name} with ${teacherName(classroom.teacher_user_id)}` : teacherName(target.teacherUserId)}.`;
  }
  return {
    denied: false,
    target,
    title,
    levelLabel,
    detail,
    stats: studentPage ? studentStats(scoped[0]) : overallStats(students),
    domains: domainRows,
    sections,
    breadcrumbs,
    missingGradeStudents:
      context.viewer.role === "ORG_ADMIN"
        ? scoped
            .filter((student) => student.grade === null)
            .map(({ user_id, name }) => ({ user_id, name }))
        : [],
  };
}
